import { createContext, useContext, useState, ReactNode } from 'react'
import { Switch } from '@mantine/core'
import { useHotkeys } from '@mantine/hooks'

interface EditModeType {
  enabled: boolean
  set: (enabled: boolean) => void
}

const EditModeContext = createContext<EditModeType>({
  enabled: false,
  set: () => {},
})

export function EditModeProvider({ children }: { children: ReactNode }) {
  const [enabled, set] = useState(false)
  return (
    <EditModeContext.Provider value={{ enabled, set }}>
      {children}
    </EditModeContext.Provider>
  )
}

export function useEditMode() {
  const context = useContext(EditModeContext)
  if (!context) {
    throw new Error('useEditMode must be used within a EditModeProvider')
  }
  return [context.enabled, context.set] as const
}

export function EditModeSwitch() {
  const [enabled, set] = useEditMode()
  useHotkeys([['mod+J', () => set(!enabled)]])
  return (
    <Switch
      checked={enabled}
      label="Edit Mode"
      onChange={(event) => {
        set(event.currentTarget.checked)
      }}
      style={{ '--label-offset-start': '0.25rem' } as React.CSSProperties}
      styles={{
        track: {
          backgroundColor: enabled
            ? 'var(--mantine-color-teal-6)'
            : 'light-dark(var(--mantine-color-gray-3), var(--mantine-color-gray-7))',
        },
      }}
    />
  )
}

export type WidgetContextType = {
  period_type?: 'year' | 'month' | 'quarter' | 'total'
  period?: string
  subject_type?: 'team' | 'user' | 'overall'
  subject?: number
  metric?: string
  component?: string
  statistic?: string
  competition_id?: number
  competition_start?: string
  competition_end?: string
}

const WidgetContext = createContext<WidgetContextType>({})

export function useWidgetContext() {
  const context = useContext(WidgetContext)
  return context
}

export function WidgetContextProvider({
  children,
  context,
}: {
  children: ReactNode
  context: WidgetContextType
}) {
  const prevContext = useWidgetContext()
  return (
    <WidgetContext.Provider value={{ ...prevContext, ...context }}>
      {children}
    </WidgetContext.Provider>
  )
}
